
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useIsMobile } from "@/hooks/use-mobile";
import ServiceCard from './Services/ServiceCard';
import { services } from './Services/data/servicesData';
import { containerVariants } from './Services/utils/serviceStyles';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Brain, Palette, TrendingUp } from 'lucide-react';
import { cn } from "@/lib/utils";

const CATEGORIES = ['strategy', 'design', 'automation'] as const;
type Category = typeof CATEGORIES[number];

// Updated category info with better contrast colors
const categoryInfo = {
  strategy: {
    label: "Strategy & Planning",
    icon: <Brain size={16} className="mr-1.5" />,
    gradientFrom: "from-[#FF6B6B]",
    gradientTo: "to-[#FF8585]",
    iconBg: "bg-white text-gray-800"
  },
  design: {
    label: "Design & Development",
    icon: <Palette size={16} className="mr-1.5" />,
    gradientFrom: "from-[#FF6B6B]",
    gradientTo: "to-[#FF8585]",
    iconBg: "bg-white text-gray-800"
  },
  automation: {
    label: "Growth & Automation",
    icon: <TrendingUp size={16} className="mr-1.5" />,
    gradientFrom: "from-[#FF6B6B]",
    gradientTo: "to-[#FF8585]",
    iconBg: "bg-white text-gray-800"
  }
};

const ServicesList = () => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<Category>('strategy');
  const [isAutoRotationPaused, setIsAutoRotationPaused] = useState(false);
  
  useEffect(() => {
    if (isAutoRotationPaused) return;

    const interval = setInterval(() => {
      setActiveTab((current) => {
        const currentIndex = CATEGORIES.indexOf(current);
        const nextIndex = (currentIndex + 1) % CATEGORIES.length;
        return CATEGORIES[nextIndex];
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoRotationPaused]);

  const groupedServices = {
    strategy: services.filter(s => 
      s.tag.includes('Strategy') || 
      s.tag.includes('Fundraising') || 
      s.tag.includes('🎯 Strategy')
    ),
    design: services.filter(s => 
      s.tag.includes('Design') || 
      s.tag.includes('Web') || 
      s.tag.includes('Shopify') || 
      s.tag.includes('🎨 Design') || 
      s.tag.includes('💻 Development')
    ),
    automation: services.filter(s => 
      s.tag.includes('AI') || 
      s.tag.includes('Tech') || 
      s.tag.includes('Marketing') || 
      s.tag.includes('Growth') || 
      s.tag.includes('🤖 AI & Automation') || 
      s.tag.includes('📈 Growth') || 
      s.tag.includes('💪 Support')
    )
  };
  
  return (
    <div className="w-full mx-auto px-0 sm:px-2" onMouseLeave={() => setIsAutoRotationPaused(false)}>
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as Category)} className="w-full">
        {isMobile ? (
          <div className="mb-5">
            <TabsList className="flex flex-col w-full gap-2 p-1 bg-gray-800/70 rounded-lg">
              {Object.entries(categoryInfo).map(([key, info]) => (
                <TabsTrigger 
                  key={key}
                  value={key}
                  className="relative w-full py-3 text-sm font-medium data-[state=active]:text-white text-white/90 transition-all duration-300 rounded-md group"
                >
                  <span className="relative z-10 flex items-center">
                    <span className={cn(
                      "inline-flex items-center justify-center w-5 h-5 rounded-full mr-1.5 transition-colors",
                      activeTab === key ? "bg-white/20" : info.iconBg
                    )}>
                      {info.icon}
                    </span>
                    {info.label}
                  </span>
                  {activeTab === key && (
                    <motion.div
                      layoutId="activeTabMobile"
                      className={cn(
                        "absolute inset-0 bg-gradient-to-r rounded-md",
                        info.gradientFrom,
                        info.gradientTo
                      )}
                      initial={false}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        ) : (
          <TabsList className="flex flex-row w-full mb-6 p-1 bg-gray-800/50 rounded-xl">
            {Object.entries(categoryInfo).map(([key, info]) => (
              <TabsTrigger 
                key={key}
                value={key}
                className="relative flex-1 px-8 py-3 data-[state=active]:text-white text-white/90 transition-all duration-300 rounded-lg"
                onMouseEnter={() => setIsAutoRotationPaused(true)}
              >
                <span className="relative z-10 flex items-center justify-center font-medium">
                  <span className={cn(
                    "inline-flex items-center justify-center w-6 h-6 rounded-full mr-2 transition-colors",
                    activeTab === key ? "bg-white/20" : info.iconBg
                  )}>
                    {info.icon}
                  </span>
                  {info.label}
                </span>
                {activeTab === key && (
                  <motion.div
                    layoutId="activeTab"
                    className={cn(
                      "absolute inset-0 bg-gradient-to-r rounded-lg",
                      info.gradientFrom,
                      info.gradientTo
                    )}
                    initial={false}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        )}

        {/* Tab content */}
        {Object.entries(groupedServices).map(([category, categoryServices]) => (
          <TabsContent key={category} value={category} className="mt-4 pt-1">
            <motion.div 
              className={`grid ${
                isMobile 
                  ? 'grid-cols-1 gap-4' 
                  : 'grid-cols-2 lg:grid-cols-4 gap-6'
              }`}
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              onMouseEnter={() => setIsAutoRotationPaused(true)}
            >
              {categoryServices.map((service, index) => (
                <ServiceCard 
                  key={index}
                  {...service}
                />
              ))}
            </motion.div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default ServicesList;
